import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { ApiService } from "@app/api/api.service";
import { ApiId, DIVE_LOG_MODEL, DiveLog, IDiveLogForm } from "@app/models";
import { selectDiveLog } from "@app/state/selectors";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { NavController } from "@ionic/angular";
import { concat, of } from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { DiveLogLineApiActions, OfflineQueueApiActions } from "../actions";
import {
  OperationApiActions,
  OperationViewPageActions,
} from "../operations/actions";
import { getApiId, getRouteId } from "../model";
import { selectOperation, selectOperationSyncOptions } from "../selectors";
import { DiveLogApiActions, DiveLogViewPageActions } from "./actions";
import { selectDiveLogId } from "./dive-logs.selectors";

@Injectable()
export class DiveLogEffects {
  private store = inject(Store);
  private router = inject(Router);
  private navController = inject(NavController);
  private apiService = inject(ApiService);
  private actions$ = inject(Actions);

  url = ["/", "dive-logs"];

  constructor() {}

  searchOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperationApiActions.searchOperationsSuccess),
      map(() => DiveLogApiActions.searchDiveLogs())
    )
  );

  searchDiveLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiveLogApiActions.searchDiveLogs),
      switchMap(() =>
        this.apiService.search(DIVE_LOG_MODEL).pipe(
          map((apiData) =>
            DiveLogApiActions.searchDiveLogsSuccess({
              diveLogs: apiData.map((item) => new DiveLog(item)),
            })
          ),
          catchError((error) =>
            of(DiveLogApiActions.searchDiveLogsFailure({ error }))
          )
        )
      )
    )
  );

  setOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiveLogViewPageActions.setDiveLog),
      concatLatestFrom(() => this.store.select(selectDiveLog)),
      map(([, { operation_id: operationId }]) =>
        DiveLogApiActions.setOperation({ operationId })
      )
    )
  );

  openOrCreateDiveLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperationViewPageActions.openDiveLogs),
      concatLatestFrom(() => [
        this.store.select(selectOperation),
        this.store.select(selectOperationSyncOptions),
      ]),
      exhaustMap(([, operation, sync]) =>
        this.apiService.searchRelated(DIVE_LOG_MODEL, "operation_id", operation).pipe(
          map((apiData) => apiData.map((item) => new DiveLog(item))),
          switchMap((diveLogs) => {
            const loaded = DiveLogApiActions.searchRelatedDiveLogsSuccess({
              diveLogs,
              operationIds: [operation._id, operation.id].filter(
                (id): id is ApiId => id !== null && id !== undefined
              ),
            });
            const operationId = getRouteId(operation);
            if (diveLogs.length > 1) {
              return concat(
                of(loaded),
                of(DiveLogApiActions.listDiveLogs({ operationId }))
              );
            }
            if (diveLogs.length === 1) {
              return concat(
                of(loaded),
                of(
                  DiveLogApiActions.viewDiveLog({ diveLog: diveLogs[0] })
                )
              );
            }
            const values: IDiveLogForm = {
              operation_id: getApiId(operation),
            };
            return concat(
              of(loaded),
              this.apiService.create(DIVE_LOG_MODEL, values, sync).pipe(
                map((apiData) =>
                  DiveLogApiActions.createDiveLogSuccess({
                    diveLog: new DiveLog(apiData),
                  })
                ),
                catchError((error) =>
                  of(DiveLogApiActions.createDiveLogFailure({ error }))
                )
              )
            );
          }),
          catchError((error) =>
            of(DiveLogApiActions.searchDiveLogsFailure({ error }))
          )
        )
      )
    )
  );

  openDiveLogListPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DiveLogApiActions.listDiveLogs),
        tap(({ operationId }) =>
          this.router.navigate([...this.url, "operation", operationId])
        )
      ),
    { dispatch: false }
  );

  openDiveLogViewPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DiveLogApiActions.viewDiveLog,
          DiveLogApiActions.createDiveLogSuccess,
          DiveLogLineApiActions.createDiveLogLineSuccess,
          DiveLogLineApiActions.updateDiveLogLineSuccess
        ),
        concatLatestFrom(() => this.store.select(selectDiveLogId)),
        tap(([{ type }, diveLogId]) => {
          const viewUrl = [...this.url, "view", diveLogId];
          if (
            type === DiveLogLineApiActions.createDiveLogLineSuccess.type ||
            type === DiveLogLineApiActions.updateDiveLogLineSuccess.type
          ) {
            void this.navController.navigateBack(viewUrl);
            return;
          }
          void this.router.navigate(viewUrl);
        })
      ),
    { dispatch: false }
  );

  syncDiveLogSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OfflineQueueApiActions.ApiCreateSuccess,
        OfflineQueueApiActions.ApiUpdateSuccess
      ),
      filter(({ model }) => model == DIVE_LOG_MODEL),
      map(({ data }) =>
        DiveLogApiActions.syncDiveLogSuccess({
          diveLog: new DiveLog(data),
        })
      )
    )
  );
}
